import React, { memo } from 'react';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import Map from '@/components/Map';
import withContentFlags from '@/hoc/withContentFlags';

interface ContactItem {
  name?: string;
  email?: string;
  label?: string;
  designation?: string;
  fax?: string;
  note?: string;
  address?: string;
  company?: string;
  website?: string;
  telephone?: string;
}

interface ContactCard {
  heading: string;
  items: ContactItem[];
}

export interface ContactDetailsProps {
  data: ContactCard[];
}

const ContactDetails = (props: ContactDetailsProps) => {
  const { data = [] } = props;

  const renderContactItem = (item: ContactItem, index: number, isLast: boolean) => {
    // Add fixed height only when neither label nor note is present
    const hasLabelOrNote = item.label || item.note;
    const topSectionClass = hasLabelOrNote
      ? 'flex flex-col gap-4'
      : 'flex flex-col gap-4 min-h-[100px]';

    return (
      <Div
        key={index}
        className={`flex flex-col ${!isLast ? 'pb-6 mb-6 border-b border-gray-200' : ''}`}
      >
        {/* Top Section - Name, Designation, Label, Note */}
        <Div className={topSectionClass}>
          {/* Name or Company */}
          {(item.name || item.company) && (
            <Heading level="2" className="text-hd-3 text-cti-primary">
              {item.name || item.company}
            </Heading>
          )}

          {/* Designation */}
          {item.designation && (
            <Paragraph className="text-body-lg text-cm-primary">{item.designation}</Paragraph>
          )}

          {/* Label */}
          {item.label && (
            <Paragraph className="text-body-sm text-cm-primary">{item.label}</Paragraph>
          )}

          {/* Note */}
          {item.note && (
            <Paragraph className="text-body-sm text-cti-tertiary">{item.note}</Paragraph>
          )}
        </Div>

        {/* Bottom Section - Address, Contact Info */}
        <Div className="flex flex-col gap-4 mt-6">
          {/* Address */}
          {item.address && (
            <Div className="flex flex-col gap-1">
              <Paragraph className="text-h-3 text-cti-primary ">Address:</Paragraph>
              <Paragraph className="text-body-base text-cm-primary font-normal">
                {item.address}
              </Paragraph>
            </Div>
          )}

          {item.telephone && (
            <Paragraph className="text-body-base text-cm-primary font-normal">
              <span className="font-medium">Tel:</span> {item.telephone}
            </Paragraph>
          )}

          {/* Fax */}
          {item.fax && (
            <Paragraph className="text-body-base text-cm-primary font-normal">
              <span className="font-medium">Fax:</span> {item.fax}
            </Paragraph>
          )}

          {/* Email */}
          {item.email && (
            <a
              href={`mailto:${item.email}`}
              className="flex items-center gap-2 text-cm-brand hover:text-cm-primary transition-colors w-fit"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path
                  d="M3.33333 5.83333L10 10.8333L16.6667 5.83333M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <Paragraph className="text-body-base font-normal">{item.email}</Paragraph>
            </a>
          )}

          {/* Website */}
          {item.website && (
            <a
              href={item.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-cm-brand hover:text-cm-primary transition-colors w-fit"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path
                  d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1.66667 10H18.3333M10 1.66667C12.0844 3.94863 13.269 6.91003 13.3333 10C13.269 13.09 12.0844 16.0514 10 18.3333C7.91561 16.0514 6.73104 13.09 6.66667 10C6.73104 6.91003 7.91561 3.94863 10 1.66667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <Paragraph className="text-body-base font-normal">
                {item.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </Paragraph>
            </a>
          )}
        </Div>
      </Div>
    );
  };

  const renderCard = (card: ContactCard, cardIndex: number) => {
    return (
      <Div
        key={cardIndex}
        className="flex flex-col p-6 gap-4 items-start self-stretch bg-white rounded-2xl border border-gray-200 w-full min-w-[calc(100%-3rem)] md:min-w-0"
      >
        {/* Card Heading */}
        {card.heading && (
          <>
            <Paragraph className="text-body-large text-cti-secondary">{card.heading}</Paragraph>
            <Div className="w-full border-b border-gray-200" />
          </>
        )}

        {/* Contact Items */}
        <Div className="flex flex-col w-full">
          <Map
            list={card.items}
            render={(item: ContactItem, index: number) =>
              renderContactItem(item, index, index === card.items.length - 1)
            }
          />
        </Div>
      </Div>
    );
  };

  return (
    <Div className="bg-cs-primary py-7 md:py-20">
      <Container className="flex flex-col gap-s7">
        {/* Title */}
        <Heading level="1" className="text-hd-1 md:text-desktop-h-1 text-cm-brand">
          Contact Details
        </Heading>

        {/* Cards Grid */}
        <Div className="flex flex-col md:flex-row gap-s6 md:grid md:grid-cols-2 lg:grid-cols-3">
          <Map list={data} render={renderCard} />
        </Div>
      </Container>
    </Div>
  );
};

export default withContentFlags(memo(ContactDetails));
